/**
 * Pure logic for editor <-> preview scroll synchronization.
 *
 * The sync is anchor-based: landmark lines in the source document (headings,
 * paragraph starts, code-fence openings) are matched against their rendered
 * block elements in the preview. Scroll positions are then interpolated
 * piecewise-linearly between anchors, so regions that render much shorter
 * than their source (a 200-line script collapsed to 15 lines in the preview)
 * or much longer (an embed directive expanding to a full component) map
 * proportionally instead of drifting.
 *
 * This file is DOM-free so the extraction/mapping logic is unit-testable;
 * the DOM glue lives in ScrollSync.ts.
 */

export type SourceAnchorKind = 'heading' | 'code' | 'text';

export interface SourceAnchor {
  /** 1-based line number in the source document */
  line: number;
  kind: SourceAnchorKind;
  /** Heading level (1-6), only set for kind 'heading' */
  level?: number;
  /** Normalized text used to locate the matching preview block */
  text: string;
}

/** Lowercase, drop unresolved {{VAR}} placeholders, punctuation, extra whitespace. */
export function normalizeMatchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/\{\{[^}]+\}\}/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Strip `1.` / `2.1.` / `0.` numbering prefixes so a source heading (`## 0. Setup`)
 * matches its numbered preview form (`## 2.1. Setup`).
 */
export function stripHeadingNumbering(text: string): string {
  return text.replace(/^\s*\d+(?:\.\d+)*\.?\s+/, '');
}

const HEADING_RE = /^(#{1,6})\s+(.+)$/;
const LIST_ITEM_RE = /^(?:[-*+]|\d+[.)])\s+(.+)$/;

/** Minimum normalized length before a line is trusted as a match anchor. */
const MIN_HEADING_CHARS = 2;
const MIN_CODE_CHARS = 4;
const MIN_TEXT_CHARS = 8;

/** How much of a paragraph-start line is used for prefix matching. */
const TEXT_ANCHOR_MAX_CHARS = 80;

/**
 * Scan the raw source document for anchor candidates. `resolveVariables`
 * substitutes `{{VAR}}` values so the text matches the woven preview.
 */
export function extractSourceAnchors(
  source: string,
  resolveVariables: (text: string) => string = (t) => t
): SourceAnchor[] {
  const anchors: SourceAnchor[] = [];
  const lines = source.split('\n');

  let inComment = false;
  let fence: { marker: string; line: number; captured: boolean } | null = null;
  let prevBlank = true;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const lineNo = i + 1;

    if (fence) {
      if (trimmed.startsWith(fence.marker)) {
        fence = null;
        prevBlank = false;
        continue;
      }
      // Anchor the fence-open line to the first non-empty code line, so a
      // long source script maps onto its (possibly collapsed) preview block.
      if (!fence.captured && trimmed) {
        fence.captured = true;
        const text = normalizeMatchText(resolveVariables(trimmed));
        if (text.length >= MIN_CODE_CHARS) {
          anchors.push({ line: fence.line, kind: 'code', text });
        }
      }
      continue;
    }

    if (inComment) {
      if (trimmed.includes('-->')) inComment = false;
      prevBlank = false;
      continue;
    }

    if (!trimmed) {
      prevBlank = true;
      continue;
    }

    // HTML comments: vars blocks, tangle/embed/collapse markers — invisible in preview
    if (trimmed.startsWith('<!--')) {
      if (!trimmed.includes('-->')) inComment = true;
      prevBlank = false;
      continue;
    }

    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      fence = { marker: trimmed.slice(0, 3), line: lineNo, captured: false };
      prevBlank = false;
      continue;
    }

    const heading = trimmed.match(HEADING_RE);
    if (heading) {
      const text = normalizeMatchText(stripHeadingNumbering(resolveVariables(heading[2])));
      if (text.length >= MIN_HEADING_CHARS) {
        anchors.push({ line: lineNo, kind: 'heading', level: heading[1].length, text });
      }
      prevBlank = true;
      continue;
    }

    if (prevBlank) {
      // Paragraph / list-item start — a mid-section anchor between headings
      const listItem = trimmed.match(LIST_ITEM_RE);
      const candidate = listItem ? listItem[1] : trimmed;
      const skip =
        candidate.startsWith('|') ||   // table rows
        candidate.startsWith('[ld:') || // leedoc markers (stripped in preview)
        candidate.startsWith('![') ||   // images (alt text unreliable)
        candidate.startsWith('<');      // raw HTML
      if (!skip) {
        const text = normalizeMatchText(resolveVariables(candidate)).slice(0, TEXT_ANCHOR_MAX_CHARS);
        if (text.length >= MIN_TEXT_CHARS) {
          anchors.push({ line: lineNo, kind: 'text', text });
        }
      }
    }
    prevBlank = false;
  }

  return anchors;
}

export interface MapPair {
  /** position in the "from" pane's scroll space */
  a: number;
  /** corresponding position in the "to" pane's scroll space */
  b: number;
}

/**
 * Keep only pairs strictly increasing in `a` and non-decreasing in `b`.
 * Input must be sorted by `a`. Drops mismatched anchors that would make the
 * mapping fold back on itself.
 */
export function toMonotonic(pairs: MapPair[]): MapPair[] {
  const out: MapPair[] = [];
  for (const p of pairs) {
    const last = out[out.length - 1];
    if (!last) {
      out.push(p);
    } else if (p.a > last.a && p.b >= last.b) {
      out.push(p);
    }
  }
  return out;
}

/** Piecewise-linear interpolation through `pairs` (monotonic in `a`); clamps at the ends. */
export function interpolate(pairs: MapPair[], x: number): number {
  if (pairs.length === 0) return x;
  if (x <= pairs[0].a) return pairs[0].b;
  const last = pairs[pairs.length - 1];
  if (x >= last.a) return last.b;

  let lo = 0;
  let hi = pairs.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (pairs[mid].a <= x) lo = mid;
    else hi = mid;
  }
  const p0 = pairs[lo];
  const p1 = pairs[hi];
  const span = p1.a - p0.a;
  if (span <= 0) return p0.b;
  return p0.b + ((x - p0.a) / span) * (p1.b - p0.b);
}

/** Swap the mapping direction (for preview -> editor sync). */
export function invertPairs(pairs: MapPair[]): MapPair[] {
  return toMonotonic(pairs.map(p => ({ a: p.b, b: p.a })));
}

/**
 * The anchor map works in content space, but the two panes usually have very
 * different content heights (a collapsed script, an expanded embed), so
 * aligning viewport TOPS is unsatisfiable near the end of the shorter pane —
 * it would need to scroll past its own max. Instead, scroll positions are
 * converted through a reference point that slides down the viewport as the
 * pane scrolls: at scrollTop 0 it's the viewport top (ref 0), at max scroll
 * it's the viewport bottom (ref = contentHeight). Mapping that reference
 * point keeps both panes in lockstep from very top to very bottom.
 */
export function referenceFromScroll(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  const maxScroll = scrollHeight - clientHeight;
  if (maxScroll <= 0) return 0;
  return (scrollTop / maxScroll) * scrollHeight;
}

/** Inverse of referenceFromScroll, clamped to the pane's scrollable range. */
export function scrollFromReference(ref: number, scrollHeight: number, clientHeight: number): number {
  const maxScroll = scrollHeight - clientHeight;
  if (maxScroll <= 0 || scrollHeight <= 0) return 0;
  const scrollTop = (ref / scrollHeight) * maxScroll;
  return Math.min(maxScroll, Math.max(0, scrollTop));
}
